import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { replaceCompanyControlUnit } from '../../api/admin';
import { useCompany, useRegister, useAuth } from '../../stores';
import invalidationManager from '../../stores/FetcherStores/InvalidationManager';
import CashRegisterZHistory from '../../components/CashRegisterZHistory';
import Loader from '../../components/loader';

const CashRegister = observer(() => {
  const companyStore = useCompany();
  const [getRegisterResponse, setGetRegisterResponse] = useState('');
  const [newControlUnitSerial, setNewControlUnitSerial] = useState('');
  const [isReplacementDialogOpen, setIsReplacementDialogOpen] = useState(false);
  const [controlUnitReplacement, setControlUnitReplacement] = useState({
    isReplacing: false,
    error: '',
    success: '',
  });
  const [showPreviousRegisters, setShowPreviousRegisters] = useState(false);
  const register = useRegister();
  const auth = useAuth();
  const company = companyStore.data || null;
  const { companyKey } = companyStore;

  useEffect(() => {
    setShowPreviousRegisters(false);
    setIsReplacementDialogOpen(false);
  }, [companyKey]);

  const handleGetRegister = async () => {
    if (!companyKey) {
      return;
    }

    const prettyJson = await register.getByCompanyKey(companyKey).catch((error) => {
      setGetRegisterResponse('');
      // eslint-disable-next-line no-alert
      alert(`Error:${error.message}`);
    });

    if (prettyJson) {
      setGetRegisterResponse(prettyJson);
    }
  };

  const handleReplaceControlUnit = async () => {
    if (!companyKey || !newControlUnitSerial.trim()) {
      return;
    }

    setIsReplacementDialogOpen(false);
    setControlUnitReplacement({ isReplacing: true, error: '', success: '' });
    try {
      const result = await replaceCompanyControlUnit(companyKey, newControlUnitSerial.trim());
      setControlUnitReplacement((current) => ({
        ...current,
        success: `New cash register created with control unit serial ${result.controlUnitSerial}.`,
      }));
      setNewControlUnitSerial('');
      await handleGetRegister();
      companyStore.loadCompany(companyKey);
      await invalidationManager.invalidate(['company', companyKey, 'z-history']);
    } catch (error) {
      setControlUnitReplacement((current) => ({
        ...current,
        error: error instanceof Error ? error.message : String(error),
      }));
    } finally {
      setControlUnitReplacement((current) => ({
        ...current,
        isReplacing: false,
      }));
    }
  };

  if (companyStore?.loading) {
    return (
      <Box className="loaderContainer">
        <Box className="loadingInnerContainer">
          <Loader />
        </Box>
      </Box>
    );
  }

  if (!company?.cashRegisterKeys) {
    return (
      <Box>
        <Typography variant="h1" gutterBottom>
          Kassaregister
        </Typography>
        <Typography>Detta företag har inget kassaregister</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Toolbar disableGutters role="toolbar" aria-label="Kassaregisterfilter">
        <FormControlLabel
          control={
            <Checkbox
              checked={showPreviousRegisters}
              onChange={(_, checked) => setShowPreviousRegisters(checked)}
            />
          }
          label="Visa tidigare kassaregister"
        />
      </Toolbar>
      <Typography variant="h1" gutterBottom>
        Kassaregister
      </Typography>

      {companyKey && (
        <CashRegisterZHistory
          key={companyKey}
          companyId={companyKey}
          showPreviousRegisters={showPreviousRegisters}
        />
      )}

      <Box>
        <Table>
          <TableBody>
            <TableRow sx={{ '& > td': { borderBottom: 'none' }, p: 0 }}>
              <TableCell>cashRegisterKeys</TableCell>
              <TableCell>
                count: {company.cashRegisterKeys?.length || 0}, keys:{' '}
                {company.cashRegisterKeys?.join(', ')}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {auth.isAdmin && (
          <Box sx={{ marginBottom: 1 }}>
            <Button
              variant="contained"
              type="button"
              className="btn btn-primary btn-lg"
              onClick={handleGetRegister}
            >
              Get/Renew Register
            </Button>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                mt: 2,
              }}
            >
              <TextField
                label="New control unit serial"
                value={newControlUnitSerial}
                onChange={(event) => setNewControlUnitSerial(event.target.value)}
                disabled={controlUnitReplacement.isReplacing}
                size="small"
                sx={{ flex: '1 1 220px' }}
              />
              <Button
                variant="outlined"
                type="button"
                onClick={() => setIsReplacementDialogOpen(true)}
                disabled={!newControlUnitSerial.trim() || controlUnitReplacement.isReplacing}
                sx={{ flexShrink: 0 }}
              >
                {controlUnitReplacement.isReplacing
                  ? 'Creating register...'
                  : 'Replace Control Unit'}
              </Button>
            </Box>
            {controlUnitReplacement.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Failed to replace control unit: {controlUnitReplacement.error}
              </Alert>
            )}
            <Dialog
              open={isReplacementDialogOpen}
              onClose={() => setIsReplacementDialogOpen(false)}
              aria-labelledby="replace-control-unit-title"
              aria-describedby="replace-control-unit-description"
            >
              <DialogTitle id="replace-control-unit-title">Replace Control Unit</DialogTitle>
              <DialogContent>
                <DialogContentText id="replace-control-unit-description">
                  Create a new cash register for this company using this control unit serial? The
                  old register will be saved in cashRegisterHistory
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setIsReplacementDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleReplaceControlUnit}
                  disabled={
                    !companyKey ||
                    !newControlUnitSerial.trim() ||
                    controlUnitReplacement.isReplacing
                  }
                >
                  Create register
                </Button>
              </DialogActions>
            </Dialog>
            {controlUnitReplacement.success && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {controlUnitReplacement.success}
              </Alert>
            )}
          </Box>
        )}
        {getRegisterResponse && (
          <Box>
            <pre>
              <code style={{ whiteSpace: 'pre-wrap' }}>{getRegisterResponse}</code>
            </pre>
          </Box>
        )}
      </Box>
    </Box>
  );
});
export default CashRegister;
